import * as React from 'react';
import { IDynScriptInjectorViewProps } from './dyn-script-injector';

type LoadPoint = 'headStart' | 'headEnd' | 'bodyStart' | 'bodyEnd';

type ScriptConfig = {
    src?: string;
    code?: string;
    async?: boolean;
    defer?: boolean;
    charset?: string;
    type?: string;
};

const useScript = (config: ScriptConfig, loadPoint: LoadPoint = 'bodyEnd') => {
    React.useEffect(() => {
        const resolvePlacement = (lp: LoadPoint): { parent: Element | null; atStart: boolean } => {
            switch (lp) {
                case 'headStart':
                    return { parent: document.querySelector('head'), atStart: true };
                case 'headEnd':
                    return { parent: document.querySelector('head'), atStart: false };
                case 'bodyStart':
                    return { parent: document.querySelector('body'), atStart: true };
                case 'bodyEnd':
                default:
                    return { parent: document.querySelector('body'), atStart: false };
            }
        };

        const { parent, atStart } = resolvePlacement(loadPoint);
        if (!parent) {
            return;
        }

        const script = document.createElement('script');
        if (config.src) {
            script.src = config.src;
            if (config.async) {
                script.async = true;
            }
            if (config.defer) {
                script.defer = true;
            }
            if (config.charset) {
                script.charset = config.charset;
            }
            script.type = config.type || 'text/javascript';
        }
        if (config.code) {
            script.textContent = config.code;
            script.type = config.type || 'text/javascript';
        }

        if (atStart && parent.firstChild) {
            parent.insertBefore(script, parent.firstChild);
        } else if (atStart && !parent.firstChild) {
            parent.appendChild(script);
        } else {
            parent.appendChild(script);
        }

        return () => {
            try {
                parent.removeChild(script);
            } catch {
                // ignore if already removed
            }
        };
    }, [config, loadPoint]);
};

export default (props: IDynScriptInjectorViewProps) => {
    const loadPoint = (props.config.loadPoint as LoadPoint) || 'bodyEnd';

    // Inject external script in its own <script> tag
    if (props.config.scriptSource) {
        useScript(
            {
                src: props.config.scriptSource,
                async: props.config.async,
                defer: props.config.defer,
                charset: 'utf-8',
                type: 'text/javascript'
            },
            loadPoint
        );
    }

    // Inject inline script in its own <script> tag
    if (props.config.scriptInline) {
        useScript(
            {
                code: props.config.scriptInline,
                type: 'text/javascript'
            },
            loadPoint
        );
    }

    return null;
};
